import { observable, computed, action } from "mobx";
import { debounce } from "lodash";
import Api from "../service/Api";
import PhotoEditingJob from "~/models/PhotoEditing/PhotoEditingJob";
import JobStatus from "~/constants/PhotoEditing/JobStatus";

const jobsApiFilters = {
  status: JobStatus.DONE,
  orderBy: "finished_dts",
  order: "desc",
};

class PhotoEditingImporter {
  @observable isLoading = false;

  @observable tourId = null;

  @observable showImporter = false;

  @observable filteredPhotoEditingJobs = [];

  @observable latestPhotoEditingJobs = [];

  @observable selectedImagesInfo = {};

  /**
   * search term for finding finished jobs
   */
  @observable photoEditingJobIdOrLabel = "";

  @observable isSearching = false;

  constructor({ tourId }) {
    if (!tourId) {
      throw new Error("Tour id expected");
    }

    this.tourId = tourId;
    this.searchPhotoEditingJobs = debounce(this.searchPhotoEditingJobs.bind(this), 500, { trailing: true });
  }

  @action
  load() {
    this.isLoading = true;

    return Api.get("/photo-editing/jobs", {
      ...jobsApiFilters,
      limit: 5,
    })
      .then(response => {
        // This widget should not show up if there are no jobs at all.
        if (response.data.photoEditingJobs.length === 0) {
          throw new Error("No latest HDR jobs found");
        }

        this.latestPhotoEditingJobs = response.data.photoEditingJobs.map(
          photoEditingJob => new PhotoEditingJob(photoEditingJob),
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @computed
  get hasSelectedImages() {
    if (Object.keys(this.selectedImagesInfo).length === 0) {
      return false;
    }

    return Object.keys(this.selectedImagesInfo).some(photoEditingJobId => {
      const imagesIds = this.selectedImagesInfo[photoEditingJobId];

      return Object.keys(imagesIds).some(photoEditingImageId => {
        return imagesIds[photoEditingImageId];
      });
    });
  }

  @computed get photoEditingJobs() {
    if (this.isSearching) {
      return [];
    }

    return this.photoEditingJobIdOrLabel ? this.filteredPhotoEditingJobs : this.latestPhotoEditingJobs;
  }

  @action
  openImporter() {
    this.showImporter = true;
    this.load();
  }

  @action
  closeImporter() {
    this.showImporter = false;
  }

  @action
  toggleImageSelection(photoEditingJobId, photoEditingImageId) {
    // we'll save only images from one job at a time.
    // We also want that when X images from job Y have been selected, and the user selects any image
    // from another job, we'll unselect all the former job's images.

    // unselect images from any other job.
    Object.keys(this.selectedImagesInfo).forEach(scopedPhotoEditingJobId => {
      // numeric objects keys are casted to string by js.
      if (String(scopedPhotoEditingJobId) !== String(photoEditingJobId)) {
        delete this.selectedImagesInfo[scopedPhotoEditingJobId];
      }
    });

    if (typeof this.selectedImagesInfo[photoEditingJobId] === "undefined") {
      this.selectedImagesInfo[photoEditingJobId] = {};
    }

    const castValue =
      typeof this.selectedImagesInfo[photoEditingJobId][photoEditingImageId] !== "undefined"
        ? this.selectedImagesInfo[photoEditingJobId][photoEditingImageId]
        : false;

    this.selectedImagesInfo[photoEditingJobId][photoEditingImageId] = !castValue;
  }

  @action
  selectAllImages(photoEditingJobId) {
    const allPhotoEditingJobs = [...this.filteredPhotoEditingJobs, ...this.latestPhotoEditingJobs];

    const photoEditingJob = allPhotoEditingJobs.find(
      ({ photoEditingJobId: scopedPhotoEditingJobId }) => scopedPhotoEditingJobId === photoEditingJobId,
    );

    if (!photoEditingJob) {
      throw new Error(`Could not find photoEditingJob for id: ${photoEditingJobId}`);
    }

    // clear, so selected images from another job are unselected
    this.selectedImagesInfo = {};
    this.selectedImagesInfo[photoEditingJobId] = {};

    photoEditingJob.photoEditingImages.forEach(({ photoEditingImageId }) => {
      this.selectedImagesInfo[photoEditingJobId][photoEditingImageId] = true;
    });
  }

  @action
  unselectAllImages() {
    this.selectedImagesInfo = {};
  }

  @action
  updateSearch(photoEditingJobIdOrLabel) {
    this.photoEditingJobIdOrLabel = photoEditingJobIdOrLabel;
    // whenever the query changes, trigger an unselect all so we avoid unexpected states.
    this.unselectAllImages();

    // return a consistent value
    return this.searchPhotoEditingJobs() || Promise.resolve();
  }

  @action
  searchPhotoEditingJobs() {
    // clear any cached filtered jobs so we can show the latest jobs in the UI
    this.filteredPhotoEditingJobs.clear();
    this.unselectAllImages();

    if (this.photoEditingJobIdOrLabel.trim() === "") {
      return Promise.resolve();
    }

    // not we don't prevent concurrent requests since this method runs in a debounce wrapper.

    this.isSearching = true;

    return Api.get(`/photo-editing/jobs`, {
      ...jobsApiFilters,
      photoEditingJobIdOrLabel: this.photoEditingJobIdOrLabel,
    })
      .then(response => {
        // clear selection just in case the user selected any image while the xhr was still in process
        this.unselectAllImages();
        this.filteredPhotoEditingJobs = response.data.photoEditingJobs.map(
          photoEditingJob => new PhotoEditingJob(photoEditingJob),
        );
      })
      .finally(() => {
        this.isSearching = false;
      });
  }

  @action
  import() {
    if (this.isLoading) {
      return Promise.resolve();
    }

    if (!this.hasSelectedImages) {
      return Promise.resolve();
    }

    // Get selected images ids
    const imagesIds = [];

    Object.keys(this.selectedImagesInfo).forEach(photoEditingJobId => {
      const info = this.selectedImagesInfo[photoEditingJobId];

      Object.keys(info).forEach(imageId => {
        // value could be false, like when an image is toggled and ends up unselected.
        // Make sure its value is truthy.
        if (info[imageId]) {
          imagesIds.push(parseInt(imageId, 10));
        }
      });
    });

    if (imagesIds.length === 0) {
      throw new Error("Did not expect to have selectedImagesInfo keys without images ids");
    }

    return Api.post(`/photo-editing/images/import-to-tour/${this.tourId}`, {
      images: imagesIds,
    });
  }
}

export default PhotoEditingImporter;
